import { getTimelineEntryDate } from '../../../services/timeline/dateUtils';

export const ALLOWED_MEDICATION_CHANGE_TYPES = new Set([
  'medication_added',
  'dose_changed',
  'schedule_changed',
  'medication_stopped',
]);

export const OBSERVATION_CATEGORIES = new Set(['behavior', 'sleep', 'mood', 'food']);

const normalizeText = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();
const lowerText = (value = '') => normalizeText(value).toLowerCase();

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValidDate(value) ? value : null;
  if (typeof value?.toDate === 'function') {
    const candidate = value.toDate();
    return isValidDate(candidate) ? candidate : null;
  }

  const date = new Date(value);
  return isValidDate(date) ? date : null;
};

export const getEntryDate = (entry = {}) => (
  getTimelineEntryDate(entry)
  || toDate(entry.timestamp)
  || toDate(entry.createdAt)
  || toDate(entry.date)
);

export const getEntryId = (entry = {}) => normalizeText(entry.id || entry.entryId || entry.docId || '');
export const getEntryChildId = (entry = {}) => normalizeText(entry.childId || entry.child?.id || '');

export const getMedicationChangeType = (entry = {}) => normalizeText(entry.medicationChangeType || entry.changeType || '');
export const getMedicationChangeAnchorTime = (entry = {}) => (
  toDate(entry.effectiveFrom)
  || toDate(entry.actualDateTime)
  || getEntryDate(entry)
);

export const isMedicationChangeAnchor = (entry = {}) => (
  !OBSERVATION_CATEGORIES.has(lowerText(entry.category))
  && ALLOWED_MEDICATION_CHANGE_TYPES.has(getMedicationChangeType(entry))
  && normalizeText(entry.medicationId)
  && getMedicationChangeAnchorTime(entry)
);

const getObservationKind = (entry = {}) => {
  const category = lowerText(entry.category);
  const type = lowerText(entry.type);
  const timelineType = lowerText(entry.timelineType);
  const actionType = lowerText(entry.actionType);
  const logCategory = lowerText(entry.logCategory);

  if (category === 'log' && timelineType === 'incident' && (entry.entryType === 'incident' || normalizeText(entry.incidentCategoryId) === 'behavior')) {
    return 'behavior';
  }

  if (category === 'sleep' || type === 'sleep' || timelineType === 'sleep' || logCategory === 'sleep') {
    return 'sleep';
  }

  if (type === 'mood' || actionType === 'mood' || category === 'mood' || logCategory === 'mood') {
    return 'mood';
  }

  if (category === 'food' || type === 'food' || logCategory === 'food') {
    return 'food';
  }

  return '';
};

const getBehaviorSeverity = (entry = {}) => {
  const value = Number(entry.severity ?? entry.incidentData?.severity);
  return Number.isFinite(value) ? value : null;
};

const getBehaviorObservation = (entry = {}) => {
  const severity = getBehaviorSeverity(entry);
  if (severity === null) return null;

  return {
    kind: 'behavior',
    id: getEntryId(entry),
    childId: getEntryChildId(entry),
    occurredAt: getEntryDate(entry),
    severity,
    severityLabel: normalizeText(entry.severityLabel || entry.incidentData?.severityLabel || ''),
    triggerSummary: normalizeText(entry.triggerSummary || entry.incidentData?.triggerSummary || ''),
    triggerNotes: normalizeText(entry.triggerNotes || entry.incidentData?.triggerNotes || ''),
    remedy: normalizeText(entry.remedy || entry.incidentData?.remedy || ''),
  };
};

const getSleepObservation = (entry = {}) => {
  const sleepDetails = entry.sleepDetails || {};
  const duration = Number(sleepDetails.duration ?? entry.duration ?? null);
  const quality = normalizeText(sleepDetails.quality || entry.sleepQuality || '');

  if (!Number.isFinite(duration) && !quality) return null;

  return {
    kind: 'sleep',
    id: getEntryId(entry),
    childId: getEntryChildId(entry),
    occurredAt: getEntryDate(entry),
    durationMinutes: Number.isFinite(duration) ? duration : null,
    quality,
    type: normalizeText(sleepDetails.type || ''),
    bedtime: normalizeText(sleepDetails.bedtime || entry.startTime || entry.startAt || ''),
    wakeTime: normalizeText(sleepDetails.wakeTime || entry.endTime || entry.endAt || ''),
    disturbances: normalizeText(sleepDetails.disturbances || ''),
  };
};

const normalizeMoodValue = (entry = {}) => {
  const raw = normalizeText(
    entry.moodValue
    || entry.value
    || entry.data?.level
    || entry.data?.value
    || ''
  );

  const lower = raw.toLowerCase();
  if (/frustrat|angry|mad|annoy|stres|upset|irritat/.test(lower)) return 'Frustrated';
  if (/sad|down|cry|tired|overwhelmed/.test(lower)) return 'Down';
  if (/neutral|\bok\b|\bokay\b|\bfine\b|\bmeh\b/.test(lower)) return 'Neutral';
  if (/calm|relaxed|peaceful|content/.test(lower)) return 'Calm';
  if (/happy|great|joyful|smiling|\bgood\b/.test(lower)) return 'Happy';
  return raw ? raw.replace(/^./, (char) => char.toUpperCase()) : '';
};

const getMoodObservation = (entry = {}) => {
  const moodValue = normalizeMoodValue(entry);
  if (!moodValue) return null;

  return {
    kind: 'mood',
    id: getEntryId(entry),
    childId: getEntryChildId(entry),
    occurredAt: getEntryDate(entry),
    moodValue,
  };
};

const classifyAppetite = ({ portion = '', reaction = '' } = {}) => {
  const combined = `${lowerText(portion)} ${lowerText(reaction)}`.trim();
  if (!combined) return '';

  if (/\b(refus|did not eat|didn't eat|would not eat|wouldn't eat|won't eat|not eating|no intake|skipped|none)\b/.test(combined)) {
    return 'refused';
  }

  if (/\b(few bites|half|partial|some|small|little|picked at|barely|a bite|bites)\b/.test(combined)) {
    return 'partial';
  }

  if (/\b(finished|ate|eaten|ate well|good appetite|normal appetite|full|most|all)\b/.test(combined)) {
    return 'eaten';
  }

  return 'unknown';
};

const getFoodObservation = (entry = {}) => {
  const foodDetails = entry.foodDetails || {};
  const mealType = normalizeText(foodDetails.mealType || entry.mealType || '');
  const portion = normalizeText(foodDetails.portion || '');
  const reaction = normalizeText(foodDetails.reaction || '');
  const appetite = classifyAppetite({ portion, reaction });

  if (!mealType || (!portion && !reaction)) return null;

  return {
    kind: 'food',
    id: getEntryId(entry),
    childId: getEntryChildId(entry),
    occurredAt: getEntryDate(entry),
    mealType,
    portion,
    reaction,
    appetite,
  };
};

export const getObservation = (entry = {}) => {
  const kind = getObservationKind(entry);
  if (!kind) return null;

  if (kind === 'behavior') return getBehaviorObservation(entry);
  if (kind === 'sleep') return getSleepObservation(entry);
  if (kind === 'mood') return getMoodObservation(entry);
  if (kind === 'food') return getFoodObservation(entry);
  return null;
};

export const sortByDateThenId = (a, b) => {
  const delta = a.occurredAt.getTime() - b.occurredAt.getTime();
  if (delta !== 0) return delta;
  return a.id.localeCompare(b.id);
};
